/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * commands.ts — commands (services/operator-web-app).
 */

/**
 * Client-to-server command builders for /ws/operator.
 * Aligned with plan/arm-operator/schemas/ws-operator-events.schema.json
 */
import type { BaseEnvelope } from './types';

const ENVELOPE_VERSION = '1.0';

function uuidv4(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function clientEnvelope(eventType: string, payload: Record<string, unknown>): BaseEnvelope {
  return {
    version: ENVELOPE_VERSION,
    direction: 'client_to_server',
    event_type: eventType,
    event_id: uuidv4(),
    occurred_at: new Date().toISOString(),
    sequence: 0,
    payload,
  };
}

export interface PresenceRegisterParams {
  operator_id: string;
  device_id: string;
  client_version: string;
  preferred_transport?: 'webrtc' | 'websocket';
  /** E8-S1: Optional canary cohort for backend routing. */
  cohort_id?: string | null;
}

export function buildPresenceRegister(params: PresenceRegisterParams): BaseEnvelope {
  return clientEnvelope('presence_register', {
    operator_id: params.operator_id,
    device_id: params.device_id,
    client_version: params.client_version,
    ...(params.preferred_transport && { preferred_transport: params.preferred_transport }),
    ...(params.cohort_id && { cohort_id: params.cohort_id }),
  });
}

export interface PresenceHeartbeatParams {
  uptime_ms: number;
  tab_visible?: boolean;
}

export function buildPresenceHeartbeat(params: PresenceHeartbeatParams): BaseEnvelope {
  return clientEnvelope('presence_heartbeat', {
    uptime_ms: params.uptime_ms,
    ...(params.tab_visible !== undefined && { tab_visible: params.tab_visible }),
  });
}

export type PauseReason = 'break' | 'training' | 'manual' | 'system';

export function buildPresencePause(reason: PauseReason): BaseEnvelope {
  return clientEnvelope('presence_pause', { reason });
}

export function buildPresenceResume(): BaseEnvelope {
  return clientEnvelope('presence_resume', {});
}

export type OfflineReason = 'logout' | 'tab_close' | 'network_lost' | 'manual';

export function buildPresenceOffline(reason: OfflineReason): BaseEnvelope {
  return clientEnvelope('presence_offline', { reason });
}

// --- Call commands (E3) ---

export function buildCallAccept(callId: string): BaseEnvelope {
  return clientEnvelope('call_accept', { call_id: callId });
}

export function buildCallReject(callId: string, reason: string): BaseEnvelope {
  return clientEnvelope('call_reject', { call_id: callId, reason });
}

export function buildCallEnd(callId: string, reason?: string): BaseEnvelope {
  return clientEnvelope('call_end', { call_id: callId, ...(reason && { reason }) });
}

// --- Wrap-up (E4-S3) ---

export interface WrapUpCompleteParams {
  call_id: string;
  outcome: string;
  required_fields_status: Record<string, boolean>;
  notes?: string;
}

export function buildWrapUpComplete(params: WrapUpCompleteParams): BaseEnvelope {
  return clientEnvelope('wrap_up_complete', {
    call_id: params.call_id,
    outcome: params.outcome,
    required_fields_status: params.required_fields_status,
    ...(params.notes !== undefined && params.notes !== '' && { notes: params.notes }),
  });
}

// --- Copilot / Hints (E6-S2) ---

export function buildHintMarkUsed(hintId: string): BaseEnvelope {
  return clientEnvelope('hint_mark_used', { hint_id: hintId });
}

export function buildHintMarkIrrelevant(hintId: string): BaseEnvelope {
  return clientEnvelope('hint_mark_irrelevant', { hint_id: hintId });
}
